import { Component } from '@theme/component';

class GroupSliderComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.layoutObserver?.disconnect();

    this.scroller = this.querySelector('[data-group-slider-scroller]');
    this.navigation = this.querySelector('[data-group-slider-navigation]');
    this.previousButton = this.querySelector('[ref="previous"]');
    this.nextButton = this.querySelector('[ref="next"]');

    if (!(this.scroller instanceof HTMLElement) || !(this.navigation instanceof HTMLElement)) return;

    this.controller = new AbortController();
    this.scroller.addEventListener('scroll', this.scheduleUpdate, {
      passive: true,
      signal: this.controller.signal,
    });

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
    this.resizeObserver.observe(this.scroller);

    this.mutationObserver = new MutationObserver(this.scheduleUpdate);
    this.mutationObserver.observe(this.scroller, { childList: true });

    const sliderBlock = this.closest('.group-block');
    if (sliderBlock?.parentElement) {
      this.layoutObserver = new MutationObserver(this.scheduleUpdate);
      this.layoutObserver.observe(sliderBlock.parentElement, { childList: true });
    }

    this.scheduleUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.layoutObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
  }

  previous(event) {
    event?.preventDefault();
    this.scrollToAdjacentPage(-1);
  }

  next(event) {
    event?.preventDefault();
    this.scrollToAdjacentPage(1);
  }

  scheduleUpdate = () => {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(this.updateControls);
  };

  updateControls = () => {
    if (!(this.scroller instanceof HTMLElement) || !(this.navigation instanceof HTMLElement)) return;

    const items = this.items;
    const hasOverflow = this.scroller.scrollWidth - this.scroller.clientWidth > 1;
    const bounds = this.scroller.getBoundingClientRect();
    const first = items[0]?.getBoundingClientRect();
    const last = items.at(-1)?.getBoundingClientRect();
    const isRtl = getComputedStyle(this.scroller).direction === 'rtl';
    const atStart = !first || (isRtl ? first.right <= bounds.right + 1 : first.left >= bounds.left - 1);
    const atEnd = !last || (isRtl ? last.left >= bounds.left - 1 : last.right <= bounds.right + 1);

    this.toggleAttribute('data-overflow', hasOverflow);
    this.navigation.hidden = !hasOverflow;
    this.updateInlineControls();

    if (this.previousButton instanceof HTMLButtonElement) {
      this.previousButton.disabled = !hasOverflow || (!this.loop && atStart);
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = !hasOverflow || (!this.loop && atEnd);
    }
  };

  updateInlineControls() {
    const sliderBlock = this.closest('.group-block');
    let previousBlock = sliderBlock?.previousElementSibling;

    while (previousBlock instanceof HTMLElement && previousBlock.getClientRects().length === 0) {
      previousBlock = previousBlock.previousElementSibling;
    }

    const canAlign = this.inlineControls && previousBlock instanceof HTMLElement;

    if (this.previousBlock !== previousBlock) {
      if (this.previousBlock instanceof HTMLElement) this.resizeObserver?.unobserve(this.previousBlock);
      this.previousBlock = previousBlock;
      if (this.previousBlock instanceof HTMLElement) this.resizeObserver?.observe(this.previousBlock);
    }

    this.toggleAttribute('data-inline-controls', canAlign);

    if (!canAlign || !(this.navigation instanceof HTMLElement)) {
      this.style.removeProperty('--group-slider-navigation-top');
      return;
    }

    const sliderBounds = this.getBoundingClientRect();
    const previousBounds = previousBlock.getBoundingClientRect();
    const navigationHeight = this.navigation.getBoundingClientRect().height;
    const navigationTop = previousBounds.top - sliderBounds.top + (previousBounds.height - navigationHeight) / 2;

    this.style.setProperty('--group-slider-navigation-top', `${navigationTop}px`);
  }

  scrollToAdjacentPage(direction) {
    if (!(this.scroller instanceof HTMLElement)) return;

    const items = this.items;
    const bounds = this.scroller.getBoundingClientRect();
    const fullyVisible = items.filter((item) => {
      const itemBounds = item.getBoundingClientRect();
      return itemBounds.left >= bounds.left - 1 && itemBounds.right <= bounds.right + 1;
    });
    const edgeItem = direction > 0 ? fullyVisible.at(-1) : fullyVisible[0];
    const edgeIndex = edgeItem ? items.indexOf(edgeItem) : 0;
    let targetIndex = direction > 0 ? edgeIndex + 1 : edgeIndex - 1;
    let wrapped = false;

    if (this.loop && targetIndex >= items.length) {
      targetIndex = 0;
      wrapped = true;
    } else if (this.loop && targetIndex < 0) {
      targetIndex = items.length - 1;
      wrapped = true;
    } else {
      targetIndex = Math.max(0, Math.min(items.length - 1, targetIndex));
    }

    const target = items[targetIndex];

    target?.scrollIntoView({
      behavior: wrapped || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }

  get loop() {
    return this.hasAttribute('loop');
  }

  get inlineControls() {
    return this.hasAttribute('inline-controls');
  }

  get items() {
    if (!(this.scroller instanceof HTMLElement)) return [];
    return Array.from(this.scroller.children).filter(
      (item) => item instanceof HTMLElement && item.getClientRects().length > 0
    );
  }
}

if (!customElements.get('group-slider-component')) {
  customElements.define('group-slider-component', GroupSliderComponent);
}
